import React, { useState } from 'react';
import {
    Button,
    CircularProgress,
    Grid,
    MenuItem,
    Select,
    TextField,
    FormControl,
    InputLabel
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import FormSection from '../../../../components/atom/FormSection';
import AutocompleteSelect from '../../../../components/atom/AutocompleteSelect';
import {
    activityAutocomplete,
    clientAutocomplete,
    agentAutocomplete,
    userGroupAutocomplete,
    onlyVendorAutocomplete
} from '../../../../autocomplete';

export const dateTypeOptions = [
    { value: 'activity', label: 'Activity Date' },
    { value: 'booking', label: 'Booking Date' },
    { value: 'transaction', label: 'Transaction Date' }
];

export const filterFields = {
    dateFrom: { key: 'dateFrom', label: 'Date From' },
    dateTo: { key: 'dateTo', label: 'Date To' },
    dateType: { key: 'dateType', label: 'Date Type' },
    selectedVendors: { key: 'selectedVendors', label: 'Vendors' },
    selectedClients: { key: 'selectedClients', label: 'Clients' },
    selectedAgents: { key: 'selectedAgents', label: 'Agents' },
    selectedActivities: { key: 'selectedActivities', label: 'Activities' },
    selectedUserGroups: { key: 'selectedUserGroups', label: 'User Groups' },
    groupBy: { key: 'groupBy', label: 'Group By' }
};

const CustomReportFiltersSidebar = ({
    filters,
    setFilters,
    onSearch,
    onExport,
    isExporting,
    total
}) => {
    const [apiError, setApiError] = useState(null);

    const handleDateChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setApiError(null);
    };

    const handleSelectChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setApiError(null);
    };

    const handleExport = async () => {
        setApiError(null);
        try {
            await onExport();
        } catch (error) {
            console.error('Export error:', error);
            setApiError('Failed to export data. Please try again.');
        }
    };

    const formDisabled = isExporting;

    return (
        <Grid container style={{ width: 300 }} spacing={1}>
            <FormSection>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth disabled={formDisabled}>
                            <InputLabel>{filterFields.dateType.label}</InputLabel>
                            <Select
                                value={filters.dateType}
                                onChange={e => handleSelectChange('dateType', e.target.value)}
                            >
                                {dateTypeOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            type="date"
                            label={filterFields.dateFrom.label}
                            fullWidth
                            value={filters.dateFrom || ''}
                            onChange={e => handleDateChange('dateFrom', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            type="date"
                            label={filterFields.dateTo.label}
                            fullWidth
                            value={filters.dateTo || ''}
                            onChange={e => handleDateChange('dateTo', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteSelect
                            initCall={onlyVendorAutocomplete.init}
                            autocompleteCall={onlyVendorAutocomplete.search}
                            value={filters.selectedVendors}
                            setValue={value => handleSelectChange('selectedVendors', value)}
                            multiple
                            disabled={formDisabled}
                        >
                            <TextField label={filterFields.selectedVendors.label} />
                        </AutocompleteSelect>
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteSelect
                            initCall={clientAutocomplete.init}
                            autocompleteCall={clientAutocomplete.search}
                            value={filters.selectedClients}
                            setValue={value => handleSelectChange('selectedClients', value)}
                            multiple
                            disabled={formDisabled}
                        >
                            <TextField label={filterFields.selectedClients.label} />
                        </AutocompleteSelect>
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteSelect
                            initCall={agentAutocomplete.init}
                            autocompleteCall={agentAutocomplete.search}
                            value={filters.selectedAgents}
                            setValue={value => handleSelectChange('selectedAgents', value)}
                            multiple
                            disabled={formDisabled}
                        >
                            <TextField label={filterFields.selectedAgents.label} />
                        </AutocompleteSelect>
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteSelect
                            initCall={activityAutocomplete.init}
                            autocompleteCall={activityAutocomplete.search}
                            value={filters.selectedActivities}
                            setValue={value => handleSelectChange('selectedActivities', value)}
                            multiple
                            disabled={formDisabled}
                        >
                            <TextField label={filterFields.selectedActivities.label} />
                        </AutocompleteSelect>
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteSelect
                            initCall={userGroupAutocomplete.init}
                            autocompleteCall={userGroupAutocomplete.search}
                            value={filters.selectedUserGroups}
                            setValue={value => handleSelectChange('selectedUserGroups', value)}
                            multiple
                            disabled={formDisabled}
                        >
                            <TextField label={filterFields.selectedUserGroups.label} />
                        </AutocompleteSelect>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth disabled={formDisabled}>
                            <InputLabel>{filterFields.groupBy.label}</InputLabel>
                            <Select
                                value={filters.groupBy}
                                onChange={e => handleSelectChange('groupBy', e.target.value)}
                            >
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="agent">Agent</MenuItem>
                                <MenuItem value="vendor">Vendor</MenuItem>
                                <MenuItem value="activity">Activity</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {apiError && (
                        <Grid item xs={12}>
                            <Alert severity="error">{apiError}</Alert>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={onSearch}
                                    disabled={formDisabled}
                                >
                                    Filter
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    size="small"
                                    variant="contained"
                                    color="default"
                                    onClick={handleExport}
                                    disabled={formDisabled || total === 0}
                                    startIcon={isExporting ? <CircularProgress size={20} /> : null}
                                >
                                    Export
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </FormSection>
        </Grid>
    );
};

export default CustomReportFiltersSidebar;
